import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { AttachFile } from '@mui/icons-material';
import axios from 'axios';
import { PART_UNIT_CONDITIONS, PART_CONDITION_LABELS, partConditionSelectValue } from '../../constants/partUnitConditions';
import { STORE_TYPES, normalizeStoreTypeValue, formatStoreTypeLabel } from '../../constants/storeTypes';
import { UNIT_METRIC_OPTIONS, unitMetricLabel } from '../../constants/unitMetrics';
import {
  INVENTORY_CATEGORIES,
  INVENTORY_CATEGORY_LABELS
} from '../../constants/inventoryCategories';
import {
  buildInventorySavePayload,
  createEmptyInventoryFormData,
  emptyUnitDetailRow,
  inventorySaveErrorMessage,
  isShelfLifeNaValue,
  mergeFormDataIntoReceivingLine,
  normalizeUnitExpiryForForm,
  seedFormDataFromInventoryItem,
  seedFormDataFromReceivingLine,
  SHELF_LIFE_NA_VALUE,
  toDateInputValue,
  unitExpiryForPersistence,
  withLineStoreTypeOnUnits
} from '../../utils/inventoryItemForm';
import {
  isQuantityDraftInput,
  parseQuantityFloat,
  unitRowCountForQuantity
} from '../../utils/quantityFloat';

/**
 * Shared inventory item form dialog.
 *
 * submitMode:
 * - 'save' — POST/PUT /inventory (inventory management page)
 * - 'return' — returns form values to parent (receiving line editor; no API write)
 */
const InventoryItemFormDialog = ({
  open,
  onClose,
  submitMode = 'save',
  item = null,
  initialLine = null,
  headerAircraft = null,
  aircraftList = [],
  storeApi = (path) => path,
  onSaved,
  onReturn,
  onDocumentsOpen,
  title,
  lockPartNumber = false,
  readOnlyOrderedQty = null,
  showCertificateFields = false
}) => {
  const isSaveMode = submitMode === 'save';
  const isReceivingMode = submitMode === 'return';
  const editingItem = isSaveMode ? item : null;

  const [formData, setFormData] = useState(createEmptyInventoryFormData());
  const [unitDetailsDialog, setUnitDetailsDialog] = useState({ open: false, rows: [] });
  const [saving, setSaving] = useState(false);
  const [quantityDraft, setQuantityDraft] = useState(undefined);

  /** Build a unit-detail row seeded with the line-level condition, store and shelf life. */
  const seedUnitRowFromLine = (source) => ({
    ...emptyUnitDetailRow(source.storeType),
    condition: partConditionSelectValue(source.condition),
    expiryDate: normalizeUnitExpiryForForm(source.expiryDate, source.shelfLifeNotApplicable)
  });

  const aircraftScopeOptions = useMemo(() => {
    const base = Array.isArray(aircraftList) ? aircraftList : [];
    const extraId = formData.aircraftId ? String(formData.aircraftId) : '';
    if (extraId && !base.some((a) => String(a._id) === extraId)) {
      return [...base, {
        _id: extraId,
        registration: formData.aircraftRegistration || extraId
      }];
    }
    return base;
  }, [aircraftList, formData.aircraftId, formData.aircraftRegistration]);

  useEffect(() => {
    if (!open) return;
    setQuantityDraft(undefined);
    if (isSaveMode) {
      setFormData(seedFormDataFromInventoryItem(item));
      return;
    }
    setFormData(seedFormDataFromReceivingLine(initialLine, headerAircraft || {}));
  }, [open, isSaveMode, item, initialLine, headerAircraft]);

  const applyQuantityToForm = (prev, qty) => {
    const existing = Array.isArray(prev.unitDetails) ? prev.unitDetails : [];
    const rowCount = unitRowCountForQuantity(qty);
    return {
      ...prev,
      quantity: qty,
      unitDetails: Array.from({ length: rowCount }, (_, idx) => (
        existing[idx] || seedUnitRowFromLine(prev)
      ))
    };
  };

  const resolveFormDataForSubmit = () => {
    if (quantityDraft === undefined) return formData;
    const qty = parseQuantityFloat(quantityDraft, { min: 0, fallback: 0 });
    return applyQuantityToForm(formData, qty);
  };

  const getQuantityDisplay = () => {
    if (quantityDraft !== undefined) return quantityDraft;
    if (formData.quantity != null) return String(formData.quantity);
    return '0';
  };

  const handleQuantityChange = (rawValue) => {
    if (!isQuantityDraftInput(rawValue)) return;
    setQuantityDraft(rawValue);
  };

  const commitQuantity = () => {
    if (quantityDraft === undefined) return;
    const qty = parseQuantityFloat(quantityDraft, { min: 0, fallback: 0 });
    setFormData((prev) => applyQuantityToForm(prev, qty));
    setQuantityDraft(undefined);
  };

  const dialogTitle = title || (
    isReceivingMode
      ? 'Line item details'
      : (editingItem ? 'Edit Inventory Item' : 'Add Inventory Item')
  );

  const handleSubmit = async () => {
    const resolvedForm = resolveFormDataForSubmit();
    if (quantityDraft !== undefined) {
      setFormData(resolvedForm);
      setQuantityDraft(undefined);
    }
    const validationError = (() => {
      if (!String(resolvedForm.partNumber || '').trim()) return 'Part number is required';
      if (!String(resolvedForm.description || '').trim()) return 'Description is required';
      if (resolvedForm.aircraftScope === 'AIRCRAFT' && !resolvedForm.aircraftId) {
        return 'Select an aircraft or switch to GEN scope';
      }
      if (resolvedForm.category === 'TOOL' && !String(resolvedForm.storePartCode || '').trim()) {
        return 'Store code is required for tools';
      }
      return null;
    })();
    if (validationError) {
      if (isSaveMode && onSaved) {
        onSaved(null, validationError);
      } else if (isReceivingMode && onReturn) {
        onReturn(null, validationError);
      }
      return;
    }

    if (isReceivingMode) {
      onReturn?.(mergeFormDataIntoReceivingLine(resolvedForm, initialLine || {}, {
        selectedPoId: readOnlyOrderedQty != null
      }));
      onClose?.();
      return;
    }

    try {
      setSaving(true);
      if (editingItem) {
        const payload = buildInventorySavePayload(resolvedForm);
        const response = await axios.put(storeApi(`/inventory/${editingItem._id}`), payload);
        onSaved?.(response.data);
      } else {
        const { status: _s, statusNotes: _n, ...createPayload } = resolvedForm;
        const payload = buildInventorySavePayload(createPayload);
        const response = await axios.post(storeApi('/inventory'), payload);
        onSaved?.(response.data, null, response.data?.merged);
      }
      onClose?.();
    } catch (error) {
      onSaved?.(null, inventorySaveErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const openUnitDetailsDialog = () => {
    const resolved = resolveFormDataForSubmit();
    if (quantityDraft !== undefined) {
      setFormData(resolved);
      setQuantityDraft(undefined);
    }
    const rowCount = unitRowCountForQuantity(resolved.quantity);
    const existing = Array.isArray(resolved.unitDetails) ? resolved.unitDetails : [];
    const rows = Array.from({ length: rowCount }, (_, idx) => {
      const existingRow = existing[idx];
      if (existingRow) {
        return {
          ...existingRow,
          storeType: normalizeStoreTypeValue(existingRow.storeType) || normalizeStoreTypeValue(resolved.storeType) || '',
          expiryDate: normalizeUnitExpiryForForm(existingRow.expiryDate, resolved.shelfLifeNotApplicable)
        };
      }
      return seedUnitRowFromLine(resolved);
    });
    setUnitDetailsDialog({ open: true, rows });
  };

  const saveUnitDetailsDialog = () => {
    const hasDatedExpiry = unitDetailsDialog.rows.some((row) => {
      const expiry = unitExpiryForPersistence(row?.expiryDate);
      return Boolean(expiry);
    });
    setFormData((prev) => ({
      ...prev,
      // If any unit gets a real date, clear the line-level N/A flag so save is consistent.
      shelfLifeNotApplicable: hasDatedExpiry ? false : prev.shelfLifeNotApplicable,
      expiryDate: hasDatedExpiry && prev.shelfLifeNotApplicable ? '' : prev.expiryDate,
      unitDetails: withLineStoreTypeOnUnits(unitDetailsDialog.rows, prev.storeType)
    }));
    setUnitDetailsDialog({ open: false, rows: [] });
  };

  const quantityLabel = isReceivingMode
    ? (readOnlyOrderedQty != null ? 'Receiving now' : 'Received quantity')
    : 'Quantity';

  const submitLabel = isReceivingMode
    ? 'Apply to line'
    : (editingItem ? 'Update' : 'Create');

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {!isReceivingMode && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Aircraft Scope</InputLabel>
                  <Select
                    value={formData.aircraftScope}
                    label="Aircraft Scope"
                    onChange={(e) => setFormData((prev) => ({
                      ...prev,
                      aircraftScope: e.target.value,
                      aircraftId: e.target.value === 'GEN' ? '' : prev.aircraftId,
                      aircraftRegistration: e.target.value === 'GEN' ? 'GEN' : prev.aircraftRegistration
                    }))}
                  >
                    <MenuItem value="GEN">GEN (common components)</MenuItem>
                    <MenuItem value="AIRCRAFT">Aircraft-specific</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {!isReceivingMode && (
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={formData.aircraftScope !== 'AIRCRAFT'}>
                  <InputLabel>Aircraft</InputLabel>
                  <Select
                    value={formData.aircraftId}
                    label="Aircraft"
                    onChange={(e) => {
                      const selected = aircraftScopeOptions.find((a) => String(a._id) === String(e.target.value));
                      setFormData((prev) => ({
                        ...prev,
                        aircraftId: e.target.value,
                        aircraftRegistration: selected?.registration || ''
                      }));
                    }}
                  >
                    {aircraftScopeOptions.map((aircraft) => (
                      <MenuItem key={aircraft._id} value={String(aircraft._id)}>
                        {aircraft.registration || aircraft._id}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Part Number"
                value={formData.partNumber}
                onChange={(e) => setFormData({ ...formData, partNumber: e.target.value })}
                required
                disabled={lockPartNumber || !!editingItem}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Reference Part Number"
                value={formData.referencePartNumber || ''}
                onChange={(e) => setFormData({ ...formData, referencePartNumber: e.target.value })}
              />
            </Grid>
            {formData.category === 'TOOL' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Store Code"
                  value={formData.storePartCode || ''}
                  onChange={(e) => setFormData({ ...formData, storePartCode: e.target.value })}
                  required
                  helperText="Manual code required for tools"
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={formData.category}
                  label="Category"
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  {INVENTORY_CATEGORIES.map((category) => (
                    <MenuItem key={category} value={category}>
                      {INVENTORY_CATEGORY_LABELS[category]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel shrink>Store Type</InputLabel>
                <Select
                  value={normalizeStoreTypeValue(formData.storeType)}
                  label="Store Type"
                  displayEmpty
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    storeType: e.target.value,
                    unitDetails: (Array.isArray(prev.unitDetails) ? prev.unitDetails : []).map((u) => ({
                      ...u,
                      storeType: e.target.value
                    }))
                  }))}
                  renderValue={(selected) => {
                    if (!selected) return 'None';
                    return formatStoreTypeLabel(selected);
                  }}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {STORE_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Condition</InputLabel>
                <Select
                  value={partConditionSelectValue(formData.condition)}
                  label="Condition"
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    condition: e.target.value,
                    unitDetails: (Array.isArray(prev.unitDetails) ? prev.unitDetails : []).map((u) => ({
                      ...u,
                      condition: e.target.value
                    }))
                  }))}
                >
                  {PART_UNIT_CONDITIONS.map((v) => (
                    <MenuItem key={v} value={v}>{PART_CONDITION_LABELS[v] || v}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Unit Metric</InputLabel>
                <Select
                  value={formData.unitMetric}
                  label="Unit Metric"
                  onChange={(e) => setFormData({ ...formData, unitMetric: e.target.value })}
                >
                  {UNIT_METRIC_OPTIONS.map((unit) => (
                    <MenuItem key={unit} value={unit}>
                      {unitMetricLabel(unit)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                required
                multiline
                rows={2}
                InputProps={{ readOnly: isReceivingMode }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Location note"
                value={formData.locationText || ''}
                onChange={(e) => setFormData({ ...formData, locationText: e.target.value })}
              />
            </Grid>
            {(readOnlyOrderedQty != null || (isSaveMode && editingItem)) && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Ordered quantity"
                  value={
                    readOnlyOrderedQty != null
                      ? readOnlyOrderedQty
                      : (Number(editingItem?.onOrderQuantity) || 0)
                  }
                  InputProps={{ readOnly: true }}
                  helperText={
                    isSaveMode && editingItem
                      ? 'Raised on a purchase order, awaiting receipt'
                      : undefined
                  }
                />
              </Grid>
            )}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={quantityLabel}
                type="number"
                value={getQuantityDisplay()}
                onChange={(e) => handleQuantityChange(e.target.value)}
                onBlur={commitQuantity}
                inputProps={{ step: 'any', min: 0 }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="outlined"
                onClick={openUnitDetailsDialog}
                disabled={Number(formData.quantity || 0) <= 0}
                sx={{ mt: 1 }}
              >
                Unit Details ({Array.isArray(formData.unitDetails) ? formData.unitDetails.length : 0})
              </Button>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label={formData.category === 'TOOL' ? 'Calibration due' : 'Shelf life / expiry'}
                type="date"
                value={formData.shelfLifeNotApplicable ? '' : formData.expiryDate}
                disabled={formData.shelfLifeNotApplicable}
                onChange={(e) => setFormData((prev) => ({
                  ...prev,
                  expiryDate: e.target.value,
                  unitDetails: (Array.isArray(prev.unitDetails) ? prev.unitDetails : []).map((u) => ({
                    ...u,
                    expiryDate: e.target.value
                  }))
                }))}
                InputLabelProps={{ shrink: true }}
                helperText={
                  formData.shelfLifeNotApplicable
                    ? 'Marked as not applicable (no shelf life / expiry).'
                    : (formData.category === 'TOOL' ? 'For tools this date is calibration due, not shelf expiry.' : undefined)
                }
              />
              {formData.category !== 'TOOL' && (
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={Boolean(formData.shelfLifeNotApplicable)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData((prev) => ({
                          ...prev,
                          shelfLifeNotApplicable: checked,
                          expiryDate: checked ? '' : prev.expiryDate,
                          unitDetails: checked
                            ? (Array.isArray(prev.unitDetails) ? prev.unitDetails : []).map((u) => ({
                              ...u,
                              expiryDate: SHELF_LIFE_NA_VALUE
                            }))
                            : (Array.isArray(prev.unitDetails) ? prev.unitDetails : []).map((u) => (
                              isShelfLifeNaValue(u?.expiryDate) ? { ...u, expiryDate: '' } : u
                            ))
                        }));
                      }}
                    />
                  )}
                  label="Not applicable (N/A)"
                />
              )}
            </Grid>
            {showCertificateFields && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Certificate Number"
                    value={formData.certificateNumber || ''}
                    onChange={(e) => setFormData({ ...formData, certificateNumber: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Remarks"
                    multiline
                    minRows={2}
                    maxRows={4}
                    value={formData.remarks || ''}
                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                  />
                </Grid>
              </>
            )}
            {editingItem && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={formData.status}
                      label="Status"
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <MenuItem value="IN_INVENTORY">In Inventory</MenuItem>
                      <MenuItem value="ISSUED">Issued</MenuItem>
                      <MenuItem value="QUARANTINED">Quarantined</MenuItem>
                      <MenuItem value="RETURNED">Returned</MenuItem>
                      <MenuItem value="UNAVAILABLE">Unavailable</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Status change notes (optional)"
                    value={formData.statusNotes}
                    onChange={(e) => setFormData({ ...formData, statusNotes: e.target.value })}
                    placeholder="Reason for status change"
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Warehouse"
                value={formData.location?.warehouse || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  location: { ...(formData.location || {}), warehouse: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Shelf"
                value={formData.location?.shelf || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  location: { ...(formData.location || {}), shelf: e.target.value }
                })}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Bin"
                value={formData.location?.bin || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  location: { ...(formData.location || {}), bin: e.target.value }
                })}
              />
            </Grid>
          </Grid>
          {isReceivingMode && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
              Changes apply to this receiving line only. Inventory is updated when the receipt is approved.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          {isSaveMode && (
            <Button
              variant="outlined"
              startIcon={<AttachFile />}
              onClick={() => editingItem && onDocumentsOpen?.(editingItem)}
              disabled={!editingItem}
            >
              Attach / Download / Delete Docs
            </Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : submitLabel}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={unitDetailsDialog.open}
        onClose={() => setUnitDetailsDialog({ open: false, rows: [] })}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Unit Details</DialogTitle>
        <DialogContent dividers>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Unit #</TableCell>
                  <TableCell sx={{ minWidth: 220 }}>Store</TableCell>
                  <TableCell>Condition</TableCell>
                  <TableCell>Batch Number</TableCell>
                  <TableCell>Serial Number</TableCell>
                  <TableCell>Shelf Life Upto</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unitDetailsDialog.rows.map((row, idx) => (
                  <TableRow key={`inv-unit-${idx}`}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Select
                        size="small"
                        fullWidth
                        displayEmpty
                        value={normalizeStoreTypeValue(row.storeType)}
                        onChange={(e) => setUnitDetailsDialog((prev) => ({
                          ...prev,
                          rows: prev.rows.map((x, i) => (i === idx ? { ...x, storeType: e.target.value } : x))
                        }))}
                        renderValue={(selected) => {
                          if (!selected) return 'Select store';
                          const opt = STORE_TYPES.find((t) => t.value === selected);
                          return opt?.label || selected;
                        }}
                      >
                        <MenuItem value="">
                          <em>Select store</em>
                        </MenuItem>
                        {STORE_TYPES.map((t) => (
                          <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                        ))}
                      </Select>
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={partConditionSelectValue(row.condition)}
                        onChange={(e) => setUnitDetailsDialog((prev) => ({
                          ...prev,
                          rows: prev.rows.map((x, i) => (i === idx ? { ...x, condition: e.target.value } : x))
                        }))}
                      >
                        {PART_UNIT_CONDITIONS.map((v) => (
                          <MenuItem key={v} value={v}>{PART_CONDITION_LABELS[v] || v}</MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.batchNumber || ''}
                        onChange={(e) => setUnitDetailsDialog((prev) => {
                          const value = e.target.value;
                          const previousValue = prev.rows[idx]?.batchNumber || '';
                          return {
                            ...prev,
                            rows: prev.rows.map((x, i) => {
                              if (i < idx) return x;
                              if (i === idx) return { ...x, batchNumber: value };
                              return (x.batchNumber || '') === previousValue
                                ? { ...x, batchNumber: value }
                                : x;
                            })
                          };
                        })}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        value={row.serialNumber || ''}
                        onChange={(e) => setUnitDetailsDialog((prev) => {
                          const value = e.target.value;
                          const previousValue = prev.rows[idx]?.serialNumber || '';
                          return {
                            ...prev,
                            rows: prev.rows.map((x, i) => {
                              if (i < idx) return x;
                              if (i === idx) return { ...x, serialNumber: value };
                              return (x.serialNumber || '') === previousValue
                                ? { ...x, serialNumber: value }
                                : x;
                            })
                          };
                        })}
                      />
                    </TableCell>
                    <TableCell>
                      {isShelfLifeNaValue(row.expiryDate) ? (
                        <TextField
                          size="small"
                          value={SHELF_LIFE_NA_VALUE}
                          onChange={(e) => {
                            const nextVal = e.target.value;
                            setUnitDetailsDialog((prev) => ({
                              ...prev,
                              rows: prev.rows.map((x, i) => (
                                i === idx
                                  ? {
                                    ...x,
                                    expiryDate: isShelfLifeNaValue(nextVal)
                                      ? SHELF_LIFE_NA_VALUE
                                      : (toDateInputValue(nextVal) || nextVal)
                                  }
                                  : x
                              ))
                            }));
                          }}
                          onFocus={(e) => e.target.select()}
                          placeholder="N/A or date"
                        />
                      ) : (
                        <TextField
                          size="small"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          value={toDateInputValue(row.expiryDate)}
                          onChange={(e) => setUnitDetailsDialog((prev) => ({
                            ...prev,
                            rows: prev.rows.map((x, i) => (
                              i === idx ? { ...x, expiryDate: e.target.value || undefined } : x
                            ))
                          }))}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUnitDetailsDialog({ open: false, rows: [] })}>Cancel</Button>
          <Button variant="contained" onClick={saveUnitDetailsDialog}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InventoryItemFormDialog;
